import type { AxiosResponse } from 'axios';

/**
 * The Company Diary API surface: entries, their types, reminders, notes and
 * attachments.
 *
 * Everything here lands on schedule chains typed `company_diary_entry`, which every
 * operational query excludes - the diary is a separate surface from the operations
 * diary, not a filtered view of it.
 *
 * Entry dates live on the entry's schedule event, never on the entry itself, so the
 * payloads below send `start_datetime` / `end_datetime` and read them back the same
 * way. `all_day` is a request-time convenience: the API expands it to the whole-day
 * sentinel range and returns the flag derived back from that range, so a round trip
 * is lossless without the client ever composing the times itself.
 */

const ROUTE = '/company-diary';

export type CompanyDiaryEntryStatus = 'TO_DO' | 'IN_PROGRESS' | 'PAUSED' | 'COMPLETED';

export type CompanyDiaryEntryTypeKey = 'STAFF_UNAVAILABILITY' | 'STAFF_LEAVE';

export interface CompanyDiarySavedAddress {
    name?: string | null;
    address: string;
}

export interface CompanyDiaryEntryType {
    id: number;
    name: string;
    colour: string | null;
    icon: string | null;
    /**
     * Set on the types the platform guarantees. Unlike `is_system`, which is only a
     * marker, this one is enforced: the API refuses to delete, deactivate or unblock
     * a type that has it.
     */
    system_key: CompanyDiaryEntryTypeKey | null;
    /** Derived from `system_key` - what the settings page disables its controls on. */
    locked: boolean;
    is_system: boolean;
    active: boolean;
    blocks_availability: boolean;
    saved_addresses?: CompanyDiarySavedAddress[];
    entries_count?: number;
}

export interface CompanyDiaryResourceSummary {
    id: number;
    name: string | null;
    /**
     * Vessels only. Containers and trailers share one `vessel_ids` payload but are
     * offered as two separate pickers, so a saved assignment needs the
     * discriminator to go back into the picker it came from.
     */
    type?: string | null;
    /**
     * Staff only - the uploaded profile photo, or the generated `ui-avatars.com`
     * fallback the API composes when there is none. The list's Staff column renders
     * the same avatar stack as the operations diary, which needs it.
     */
    avatar?: string | null;
}

/** The four pickers the entry modal offers, as the availability route spells them. */
export type CompanyDiaryResourceKindKey = 'staff' | 'transport' | 'container' | 'trailer';

/**
 * One clash behind a resource's `Conflict` badge.
 *
 * A detail is either a scheduled event or a hand-created block-out, and the two
 * carry different halves of this shape: a block-out has a `label` and a window and
 * nothing else, because that is all a block-out row holds. Everything past
 * `is_diary_entry` is therefore optional by nature rather than by defensiveness.
 */
export interface CompanyDiaryAvailabilityDetail {
    id?: number;
    /** A block-out's name, or the event's `#REF - Customer` summary. */
    label?: string;
    /** The card's heading: the customer, or a diary entry's own title. */
    heading?: string;
    /** `ScheduleServiceEventType` value, for the lozenge's icon and colour. */
    type?: string;
    action_lozenge_label?: string | null;
    is_diary_entry?: boolean;
    /** A clashing diary entry's own type - badge, not the generic MISCELLANEOUS one. */
    entry_type?: { name?: string | null; colour?: string | null; icon?: string | null } | null;
    order_ref?: string | null;
    customer_name?: string | null;
    /** Absent for staff and for diary blockouts, which allocate nothing. */
    allocated_capacity?: string | null;
    location?: string | null;
    /** A transit's far end; null on everything else. */
    end_location?: string | null;
    /** Names the wall clock below, never shifts it - these windows are naive-local. */
    timezone_code?: string | null;
    start_datetime?: string | null;
    end_datetime?: string | null;
}

export interface CompanyDiaryAvailabilityReason {
    type: string;
    status: 'available' | 'unavailable' | 'warning' | 'recommended';
    message: string;
    details?: CompanyDiaryAvailabilityDetail[];
}

export interface CompanyDiaryAssignableResource {
    id: number;
    kind: CompanyDiaryResourceKindKey;
    name: string;
    subtitle: string | null;
    /** Null when no window was supplied - "not asked", distinct from "free". */
    is_available: boolean | null;
    availability_reasons: CompanyDiaryAvailabilityReason[];
}

export interface CompanyDiaryEntry {
    staff_dates_are_local?: boolean;
    id: number;
    title: string;
    description: string | null;
    status: CompanyDiaryEntryStatus;
    type?: Pick<CompanyDiaryEntryType, 'id' | 'name' | 'colour' | 'icon' | 'blocks_availability' | 'active'>;
    company_diary_entry_type_id: number;
    start_datetime: string | null;
    end_datetime: string | null;
    all_day: boolean;
    is_scheduled: boolean;
    depot_id: number | null;
    schedule_service_id: number | null;
    schedule_service_event_id: number | null;
    blocks_availability: boolean;
    staff: CompanyDiaryResourceSummary[];
    transports: CompanyDiaryResourceSummary[];
    vessels: CompanyDiaryResourceSummary[];
    created_by: number | null;
    creator?: { id: number | null; name: string | null };
    addresses: CompanyDiaryAddress[];
    sourceable_type: string | null;
    sourceable_id: number | null;
    created_at?: string | null;
    updated_at?: string | null;
}

export interface CompanyDiaryEntryReminder {
    document_timing?: { timezone: string; days_before: number; time: string } | null;
    paused?: boolean;
    id: number;
    schedule_service_event_id: number;
    remind_at: string;
    sent_at: string | null;
    is_sent: boolean;
    recipient_user_ids?: number[];
    recipient_roles?: string[];
}

/** One location on an entry, as the API returns it. */
export interface CompanyDiaryAddress {
    id: number;
    /** The text the CRM renders and hands straight back on save. */
    label: string | null;
    order: number | null;
}

/** The kinds of resource a diary history can be scoped to. */
export type CompanyDiaryResourceKind = 'staff' | 'transport' | 'vessel';

export interface CompanyDiaryEntryPayload {
    company_diary_entry_type_id?: number;
    title?: string;
    description?: string | null;
    status?: CompanyDiaryEntryStatus;
    start_datetime?: string | null;
    end_datetime?: string | null;
    all_day?: boolean;
    depot_id?: number | null;
    /**
     * An ordered list of address strings. Absent leaves the entry's locations
     * alone; an empty array clears them. `type` and `order` are the server's to
     * stamp - a diary entry has no start and end to choose between.
     */
    addresses?: string[];
    staff_ids?: number[];
    transport_ids?: number[];
    vessel_ids?: number[];
}

function indexEntries(params?: Record<string, unknown>): Promise<AxiosResponse> {
    return window.tenantAxios.get(`${ROUTE}/entries`, { params });
}

function showEntry(id: number): Promise<AxiosResponse> {
    return window.tenantAxios.get(`${ROUTE}/entries/${id}`);
}

function createEntry(data: CompanyDiaryEntryPayload): Promise<AxiosResponse> {
    return window.tenantAxios.post(`${ROUTE}/entries`, data);
}

function updateEntry(id: number, data: CompanyDiaryEntryPayload): Promise<AxiosResponse> {
    return window.tenantAxios.patch(`${ROUTE}/entries/${id}`, data);
}

function destroyEntry(id: number): Promise<AxiosResponse> {
    return window.tenantAxios.delete(`${ROUTE}/entries/${id}`);
}

/**
 * One resource's diary history - a truck's maintenance and damage record, a staff
 * member's leave. Same filters as the main listing, scoped by the route.
 */
function resourceHistory(
    kind: CompanyDiaryResourceKind,
    resourceId: number,
    params?: Record<string, unknown>,
): Promise<AxiosResponse> {
    return window.tenantAxios.get(`${ROUTE}/resources/${kind}/${resourceId}/entries`, { params });
}

/**
 * Assignable resources of one kind, with their availability for a window.
 *
 * Window-rooted rather than event-rooted: an entry being created has no event yet,
 * and every operations-diary availability endpoint requires an `event_id`. Pass
 * `exclude_entry_id` when editing, or the entry reports its own booking as the
 * clash.
 *
 * `all_day` is encoded here rather than by the caller because axios serialises a
 * query-string boolean as the word `true`/`false`, and Laravel's `boolean` rule
 * accepts only `1`/`0`. Sending the boolean 422s the whole request, and the picker
 * renders that as an empty roster - indistinguishable from a company that owns no
 * trucks. Encoded at the boundary so no caller can reintroduce it.
 */
function resourceAvailability(
    kind: CompanyDiaryResourceKindKey,
    params?: Record<string, unknown>,
): Promise<AxiosResponse> {
    const query = { ...(params ?? {}) };

    if ('all_day' in query) {
        query.all_day = query.all_day ? 1 : 0;
    }

    return window.tenantAxios.get(`${ROUTE}/resource-availability/${kind}`, { params: query });
}

function indexTypes(params?: Record<string, unknown>): Promise<AxiosResponse> {
    return window.tenantAxios.get(`${ROUTE}/entry-types`, { params });
}

function createType(data: Partial<CompanyDiaryEntryType>): Promise<AxiosResponse> {
    return window.tenantAxios.post(`${ROUTE}/entry-types`, data);
}

function updateType(id: number, data: Partial<CompanyDiaryEntryType>): Promise<AxiosResponse> {
    return window.tenantAxios.patch(`${ROUTE}/entry-types/${id}`, data);
}

function destroyType(id: number): Promise<AxiosResponse> {
    return window.tenantAxios.delete(`${ROUTE}/entry-types/${id}`);
}

function indexReminders(entryId: number): Promise<AxiosResponse> {
    return window.tenantAxios.get(`${ROUTE}/entries/${entryId}/reminders`);
}

function createReminder(entryId: number, data: Record<string, unknown>): Promise<AxiosResponse> {
    return window.tenantAxios.post(`${ROUTE}/entries/${entryId}/reminders`, data);
}

function updateReminder(entryId: number, reminderId: number, data: Record<string, unknown>): Promise<AxiosResponse> {
    return window.tenantAxios.patch(`${ROUTE}/entries/${entryId}/reminders/${reminderId}`, data);
}

function destroyReminder(entryId: number, reminderId: number): Promise<AxiosResponse> {
    return window.tenantAxios.delete(`${ROUTE}/entries/${entryId}/reminders/${reminderId}`);
}

function indexNotes(entryId: number, params?: Record<string, unknown>): Promise<AxiosResponse> {
    return window.tenantAxios.get(`${ROUTE}/entries/${entryId}/notes`, { params });
}

function createNote(entryId: number, data: Record<string, unknown>): Promise<AxiosResponse> {
    return window.tenantAxios.post(`${ROUTE}/entries/${entryId}/notes`, data);
}

function updateNote(entryId: number, noteId: number, data: Record<string, unknown>): Promise<AxiosResponse> {
    return window.tenantAxios.patch(`${ROUTE}/entries/${entryId}/notes/${noteId}`, data);
}

function indexFiles(entryId: number): Promise<AxiosResponse> {
    return window.tenantAxios.get(`${ROUTE}/entries/${entryId}/files`);
}

function attachFile(entryId: number, data: Record<string, unknown>): Promise<AxiosResponse> {
    return window.tenantAxios.post(`${ROUTE}/entries/${entryId}/files`, data);
}

/**
 * A short-lived signed URL for one attachment. Generated per request rather than
 * stored - a damage photo or a police check is tenant-private.
 */
function viewFile(entryId: number, fileId: number): Promise<AxiosResponse> {
    return window.tenantAxios.get(`${ROUTE}/entries/${entryId}/files/${fileId}/view`);
}

/** Streamed through the API so the download carries the operator's own label. */
function downloadFile(entryId: number, fileId: number): Promise<AxiosResponse> {
    return window.tenantAxios.get(`${ROUTE}/entries/${entryId}/files/${fileId}/download`, {
        responseType: 'blob',
    });
}

/** Renames an attachment and re-tags it. The stored object is untouched. */
function updateFile(entryId: number, fileId: number, data: Record<string, unknown>): Promise<AxiosResponse> {
    return window.tenantAxios.patch(`${ROUTE}/entries/${entryId}/files/${fileId}`, data);
}

function destroyFile(entryId: number, fileId: number): Promise<AxiosResponse> {
    return window.tenantAxios.delete(`${ROUTE}/entries/${entryId}/files/${fileId}`);
}

export default {
    indexEntries,
    showEntry,
    createEntry,
    updateEntry,
    destroyEntry,
    resourceHistory,
    resourceAvailability,
    indexTypes,
    createType,
    updateType,
    destroyType,
    indexReminders,
    createReminder,
    updateReminder,
    destroyReminder,
    indexNotes,
    createNote,
    updateNote,
    indexFiles,
    attachFile,
    viewFile,
    downloadFile,
    updateFile,
    destroyFile,
};
